import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  type User,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { dataAuth, iamAuth } from '@/lib/firebase'
import { environment } from '@/config/environment'

interface AuthState {
  user: User | null
  initializing: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  sendReset: (email: string) => Promise<void>
  /** Fresh ID token for backend bearer auth. Refreshes if near
   *  expiry. Returns null when signed out. */
  getIdToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

async function bridgeIntoDataProject(iamUser: User): Promise<void> {
  if (dataAuth.currentUser?.uid === iamUser.uid) return
  const idToken = await iamUser.getIdToken()
  const res = await fetch(`${environment.apiBaseURL}/auth/data-token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  if (!res.ok) {
    throw new Error(`data-token bridge failed: HTTP ${res.status}`)
  }
  const { customToken } = (await res.json()) as { customToken: string }
  await signInWithCustomToken(dataAuth, customToken)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    // Complete any in-flight Google redirect sign-in. We use signInWithRedirect
    // (not popup) because mobile browsers block/close auth popups (#67). The
    // resulting session is delivered via onAuthStateChanged below; this call
    // processes the redirect return and surfaces any error.
    getRedirectResult(iamAuth).catch((err) => {
      console.error('Google redirect sign-in failed:', err)
    })
    return onAuthStateChanged(iamAuth, async (u) => {
      if (u) {
        try {
          await bridgeIntoDataProject(u)
        } catch (err) {
          console.error('Firestore data-project bridge failed:', err)
        }
      } else if (dataAuth.currentUser) {
        await fbSignOut(dataAuth).catch(() => undefined)
      }
      setUser(u)
      setInitializing(false)
    })
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      initializing,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(iamAuth, email, password)
      },
      signUp: async (email, password) => {
        await createUserWithEmailAndPassword(iamAuth, email, password)
      },
      signInWithGoogle: async () => {
        // Redirect (not popup) — mobile Chrome blocks popups. Navigates away
        // and returns to the app; getRedirectResult (above) completes it (#67).
        const provider = new GoogleAuthProvider()
        await signInWithRedirect(iamAuth, provider)
      },
      signOut: async () => {
        await Promise.all([
          fbSignOut(iamAuth),
          dataAuth.currentUser ? fbSignOut(dataAuth) : Promise.resolve(),
        ])
      },
      sendReset: async (email) => {
        await sendPasswordResetEmail(iamAuth, email)
      },
      getIdToken: async () => {
        const current = iamAuth.currentUser
        if (!current) return null
        return current.getIdToken()
      },
    }),
    [user, initializing],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
